"use client"

import React from "react"
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table"
import TableHeader from "components/TableHeader"
import TableCell from "components/TableCell"
import type { EmployeeLeavesDetailsType } from "types/leaves-types"
import Table from "components/Table"
import { usePermissions } from "app/hooks";


interface LeavesStatusTablePropsType {
  tableData: EmployeeLeavesDetailsType[] | []
  tableHeading: string
  onRequestDelete?: (leave: EmployeeLeavesDetailsType) => void
  showDelete?: boolean
  totalCount?: number
  currentPage?: number
  pageSize?: number
  hasNextPage?: boolean
  hasPreviousPage?: boolean
  onNextPage?: () => void
  onPreviousPage?: () => void
  onPageSizeChange?: (size: number) => void
}

const LeavesStatusTable: React.FC<LeavesStatusTablePropsType> = ({
  tableData,
  tableHeading,
  onRequestDelete,
  showDelete = false,
  totalCount,
  currentPage,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
}) => {
  const columnHelper = createColumnHelper<EmployeeLeavesDetailsType>()
  const permissions = usePermissions() || [];
  const canDelete = showDelete && permissions.includes("leaves.delete_employeeleavesdetails")

  // Helper function to calculate working days between dates (excludes weekends)
  const calculateWorkingDays = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0

    const start = new Date(startDate)
    const end = new Date(endDate)

    if (end < start) return 0

    let workingDaysCount = 0
    const currentDate = new Date(start)

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay()
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDaysCount++
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return workingDaysCount
  }

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }
  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }
  // Helper function to get status badge
  const getStatusBadge = (status: string | null | undefined) => {
    if (!status) {
      return (
        <span className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 px-2 py-0.5 rounded-full text-[10px] font-medium">
          Unknown
        </span>
      );
    }
    
    // Normalize status to uppercase string
    const normalizedStatus = String(status).toUpperCase().trim();
    
    const statusConfig = {
      A: {
        label: "Approved",
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        icon: "fas fa-check-circle",
      },
      P: {
        label: "Pending",
        className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
        icon: "fas fa-clock",
      },
      D: {
        label: "Declined",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-times-circle",
      },
      MA: {
        label: "Manager Approved",
        className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
        icon: "fas fa-user-check",
      },
      MD: {
        label: "Manager Declined",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-user-times",
      },
      CA: {
        label: "Client Approved",
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        icon: "fas fa-check-circle",
      },
      CR: {
        label: "Client Rejected",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-times-circle",
      },
    }

    const config = statusConfig[normalizedStatus as keyof typeof statusConfig]
    if (!config) {
      return (
        <span className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 px-2 py-0.5 rounded-full text-[10px] font-medium">
          Unknown
        </span>
      )
    }

    return (
      <span className={`${config.className} px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 w-fit`}>
        <i className={`${config.icon} text-[9px]`}></i>
        {config.label}
      </span>
    )
  }

  // Helper function to get leave type icon
  const getLeaveTypeIcon = (leaveType: string) => {
    const type = leaveType?.toLowerCase()
    if (type?.includes("sick")) return "fas fa-user-injured"
    if (type?.includes("annual") || type?.includes("vacation")) return "fas fa-umbrella-beach"
    if (type?.includes("maternity") || type?.includes("paternity")) return "fas fa-baby"
    if (type?.includes("emergency")) return "fas fa-exclamation-triangle"
    return "fas fa-calendar-day"
  }

  const columns: ColumnDef<EmployeeLeavesDetailsType, any>[] = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => <TableHeader>ID</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">#{info.getValue()}</span>
        </TableCell>
      ),
    }),

    columnHelper.accessor("leave_type_description", {
      id: "leave_type_description",
      header: () => <TableHeader>TYPE</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <i className={`${getLeaveTypeIcon(info.getValue())} text-blue-600 dark:text-blue-400 text-[10px]`}></i>
            </div>
            <span className="font-medium text-gray-900 dark:text-white text-xs">{info.getValue()}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.accessor("requested_at", {
      id: "requested_at",
      header: () => <TableHeader>REQUESTED</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <i className="fas fa-clock text-gray-400 text-[10px]"></i>
            <span className="text-gray-900 dark:text-white text-xs">{formatDateTime(info.getValue())}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.accessor("leave_status", {
      id: "leave_status",
      header: () => <TableHeader>STATUS</TableHeader>,
      cell: (info) => <TableCell>{getStatusBadge(info.getValue())}</TableCell>,
    }),

    columnHelper.accessor("start_date", {
      id: "start_date",
      header: () => <TableHeader>START</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <i className="fas fa-calendar-alt text-gray-400 text-[10px]"></i>
            <span className="text-gray-900 dark:text-white text-xs">{formatDate(info.getValue())}</span>
          </div>
        </TableCell>
      ),
    }),

    columnHelper.accessor("end_date", {
      id: "end_date",
      header: () => <TableHeader>END</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <i className="fas fa-calendar-alt text-gray-400 text-[10px]"></i>
            <span className="text-gray-900 dark:text-white text-xs">{formatDate(info.getValue())}</span>
          </div>
        </TableCell>
      ),
    }),

    columnHelper.display({
      id: "total_working_days",
      header: () => <TableHeader>TOTAL WORKING DAYS</TableHeader>,
      cell: (info) => {
        const row = info.row.original
        const workingDays = calculateWorkingDays(row.start_date, row.end_date)
        return (
          <TableCell>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-calendar-week text-gray-400 dark:text-gray-400 text-[10px]"></i>
              <span className="font-semibold text-gray-900 dark:text-gray-400 text-xs">
                {workingDays} {workingDays === 1 ? "day" : "days"}
              </span>
            </div>
          </TableCell>
        )
      },
    }),

    columnHelper.accessor("reason", {
      id: "reason",
      header: () => <TableHeader>REASON</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="max-w-[150px]">
            <p className="text-gray-900 dark:text-white truncate text-xs" title={info.getValue()}>
              {info.getValue() || "-"}
            </p>
          </div>
        </TableCell>
      ),
    }),

    columnHelper.accessor("comments", {
      id: "comments",
      header: () => <TableHeader>COMMENTS</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="max-w-[150px]">
            {info.getValue() ? (
              <div className="flex items-start gap-1.5">
                <i className="fas fa-comment text-gray-400 text-[10px] mt-0.5"></i>
                <p className="text-gray-600 dark:text-gray-300 text-xs truncate" title={info.getValue()}>
                  {info.getValue()}
                </p>
              </div>
            ) : (
              <span className="text-gray-400 text-xs">No comments</span>
            )}
          </div>
        </TableCell>
      ),
    }),
  ]
  if (canDelete) {
    columns.push({
      id: "actions",
      header: () => (
        <TableHeader>
          <div className="flex justify-center w-10">ACTION</div>
        </TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          <div className="flex justify-center w-10">
            <button
              onClick={() => onRequestDelete?.(info.row.original)}
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              title="Delete Leave"
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        </TableCell>
      ),
    })
  }
  return (
    <Table<EmployeeLeavesDetailsType> 
      tableData={tableData} 
      columns={columns} 
      tableHeading={tableHeading}
      totalCount={totalCount}
      currentPage={currentPage}
      pageSize={pageSize}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      onPageSizeChange={onPageSizeChange}
    />
  )
}

export default LeavesStatusTable
